// @ts-check
// Ficha da instituição no painel de suporte, em abas: Resumo (dados da escola, do cliente no Asaas e origem do
// cadastro), Assinatura (situação, ajustes e módulos), Cobranças, Notas fiscais (com a nota automática), Equipe e uso,
// Operação (backups e auditoria) e Suporte (entrar como suporte).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { navigate, searchParamsState, updateSearchParams } from '../../../app/router.js';
import { fetchAdminTenant, overrideAdminSubscription, startSupportSession } from '../../../data/operations.js';
import { deleteAdminTenant, fetchAdminTenantBilling, removeAdminInvoiceSettings } from '../../../data/admin-finance.js';
import { formatDate, formatDateTime } from '../../../lib/format.js';
import { ADMIN_INSTITUTION_TABS, adminPath, resolveInstitutionTab } from '../../../lib/admin-navigation.js';
import { auditActionLabel } from '../../../lib/audit-labels.js';
import { Button } from '../../../ui/button.js';
import { Tag } from '../../../ui/chip.js';
import { Modal } from '../../../ui/modal.js';
import { TabList } from '../../../ui/tab-list.js';
import { showSuccessSnackbar } from '../../../ui/snackbar.js';
import { formatByteSize, showOperationsError } from '../operations-format.js';
import { TenantModulesSection } from '../tenant-modules-section.js';
import { BackupTable } from './admin-platform-pages.js';
import { SelectField, TextField } from '../../settings/integration-ui.js';
import { AdminEmpty, AdminLoading, AdminPage, AdminSection, DefinitionList, ExternalLink, ProviderNotice, SiteOrderStatusTag, StatGrid, StatTile, SubscriptionStatusTag, billingTypeLabel, enumLabel, formatDay, formatInteger, formatMoney, formatTaxDocument } from './admin-ui.js';
import { PaymentsList } from './admin-payments-page.js';
import { InvoicesList } from './admin-invoices-page.js';
import { InvoiceSettingsDialog } from './invoice-dialogs.js';
import { Link } from '../../../app/router.js';
import { t } from '../../../i18n/index.js';

const TRIAL_EXTENSION_DAYS = 14;

/** @param {string} key @param {Record<string, unknown>} [values] */
const text = (key, values) => t(`operations.admin.panel.institution.${key}`, values);

/**
 * Origem do cadastro (contrato 08, seção 7.1): código da instituição, código global ou ambiente local.
 * @param {{ source: string, codeHint?: string, label?: string, existingUser?: boolean } | null} origin
 */
function describeSignUpOrigin(origin) {
  if (!origin) return t('operations.admin.signUpOrigin.UNKNOWN');
  const sourceText = origin.source === 'CODE'
    ? origin.label
      ? t('operations.admin.signUpOrigin.CODE_LABEL', { hint: origin.codeHint ?? '', label: origin.label })
      : t('operations.admin.signUpOrigin.CODE', { hint: origin.codeHint ?? '' })
    : t(`operations.admin.signUpOrigin.${['GLOBAL', 'LOCAL_DEV'].includes(origin.source) ? origin.source : 'UNKNOWN'}`);
  return origin.existingUser ? `${sourceText} · ${t('operations.admin.signUpOrigin.existingUser')}` : sourceText;
}

/** @param {{ detail: any, billing: any }} props */
function SummaryTab({ detail, billing }) {
  const customer = billing?.customer;
  const subscription = detail.subscription;
  return html`<div class="admin-tab-body">
    <${StatGrid} columns=${4}>
      <${StatTile} label=${t('operations.admin.usageCounts.person')} value=${formatInteger(detail.usage?.recordCounts?.person)} icon="IconUsers" />
      <${StatTile} label=${t('operations.admin.usageCounts.enrollmentOpportunity')} value=${formatInteger(detail.usage?.recordCounts?.enrollmentOpportunity)} icon="IconList" />
      <${StatTile} label=${t('operations.admin.messages')} value=${formatInteger(detail.usage?.messageCount)} icon="IconMail" />
      <${StatTile} label=${t('operations.admin.lastActivity')} value=${detail.usage?.lastActivityAt ? formatDateTime(detail.usage.lastActivityAt) : '-'} icon="IconClock" />
    </${StatGrid}>
    <div class="admin-two-columns">
      <${AdminSection} title=${text('institutionData')}>
        <${DefinitionList} columns=${1} items=${[
          { label: text('name'), value: detail.tenant.displayName },
          { label: text('address'), value: detail.tenant.slug, mono: true },
          { label: text('createdAt'), value: formatDateTime(detail.tenant.createdAt) },
          { label: text('activationStatus'), value: enumLabel('activationStatuses', detail.tenant.activationStatus) },
          { label: t('operations.admin.signUpOrigin.title'), value: describeSignUpOrigin(detail.signUpOrigin ?? null) },
          { label: text('plan'), value: detail.planName ?? subscription?.planKey ?? '-' },
          { label: text('packageVersion'), value: detail.usage?.packageVersion ?? '-' },
        ]} />
      </${AdminSection}>
      <${AdminSection} title=${text('customerData')} description=${text('customerDescription')}>
        ${!billing ? html`<${AdminLoading} rows=${2} />` : html`
          <${ProviderNotice} isProviderConfigured=${billing.isProviderConfigured} error=${billing.providerError} />
          ${customer ? html`<${DefinitionList} columns=${1} items=${[
            { label: text('customerName'), value: customer.name },
            { label: text('customerDocument'), value: formatTaxDocument(customer.cpfCnpj) },
            { label: text('customerEmail'), value: customer.email || '-' },
            { label: text('customerPhone'), value: customer.mobilePhone || customer.phone || '-' },
            { label: text('customerAddress'), value: [customer.address, customer.addressNumber, customer.complement, customer.province].filter(Boolean).join(', ') || '-' },
            { label: text('customerCity'), value: [customer.cityName, customer.state].filter(Boolean).join(' / ') || '-' },
            { label: text('customerPostalCode'), value: customer.postalCode || '-' },
            { label: text('customerId'), value: customer.id, mono: true },
          ]} />` : billing.isProviderConfigured ? html`<p class="admin-muted">${text('noCustomer')}</p>
            ${subscription ? html`<${DefinitionList} columns=${1} items=${[
              { label: text('customerName'), value: subscription.billingName || '-' },
              { label: text('customerDocument'), value: formatTaxDocument(subscription.billingDocument) },
              { label: text('customerEmail'), value: subscription.billingEmail || '-' },
            ]} />` : null}` : null}
        `}
      </${AdminSection}>
    </div>
  </div>`;
}

/** @param {{ tenantId: string, detail: any, billing: any, onChanged: () => void }} props */
function SubscriptionTab({ tenantId, detail, billing, onChanged }) {
  const [isWorking, setIsWorking] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [planKey, setPlanKey] = useState('');
  const subscription = detail.subscription;
  const state = detail.subscriptionState;
  /** @type {Array<{ key: string, name: string, priceCents: number, period: string }>} */
  const plans = detail.plans ?? [];
  const isLegacyPlan = Boolean(subscription?.planKey) && !plans.some((plan) => plan.key === subscription.planKey);
  const baseTrialEnd = subscription?.trialEndsAt && subscription.trialEndsAt > new Date().toISOString() ? new Date(subscription.trialEndsAt) : new Date();
  const extendedTrialEnd = new Date(baseTrialEnd.getTime() + TRIAL_EXTENSION_DAYS * 24 * 60 * 60 * 1000).toISOString();

  /** @param {{ status?: string, trialEndsAt?: string, planKey?: string }} changes */
  const adjust = async (changes) => {
    setIsWorking(true);
    try {
      await overrideAdminSubscription(tenantId, changes);
      showSuccessSnackbar(t('operations.admin.subscriptionUpdated'));
      setConfirmSuspend(false);
      setPlanKey('');
      onChanged();
    } catch (error) {
      showOperationsError(error);
    } finally {
      setIsWorking(false);
    }
  };

  const providerSubscription = billing?.providerSubscription;
  return html`<div class="admin-tab-body">
    <div class="admin-two-columns">
      <${AdminSection} title=${text('subscriptionData')}>
        <div class="admin-headline"><${SubscriptionStatusTag} status=${state.status} /><span class="settings-table-muted">${t(`operations.billing.accessModes.${state.accessMode}`)}</span></div>
        <${DefinitionList} columns=${1} items=${[
          { label: text('plan'), value: detail.planName ?? subscription?.planKey ?? '-' },
          { label: t('operations.billing.billingType'), value: billingTypeLabel(subscription?.billingType) },
          { label: t('operations.billing.trialEndsAt'), value: subscription?.trialEndsAt ? formatDate(subscription.trialEndsAt) : '-' },
          { label: t('operations.billing.paidUntil'), value: subscription?.currentPeriodEndsAt ? formatDate(subscription.currentPeriodEndsAt) : '-' },
          state.readOnlyAt ? { label: t('operations.billing.readOnlyAt'), value: formatDate(state.readOnlyAt) } : null,
          subscription?.pastDueSince ? { label: text('pastDueSince'), value: formatDateTime(subscription.pastDueSince) } : null,
          subscription?.suspendedAt ? { label: text('suspendedAt'), value: formatDateTime(subscription.suspendedAt) } : null,
          subscription?.canceledAt ? { label: text('canceledAt'), value: formatDateTime(subscription.canceledAt) } : null,
        ]} />
        <div class="admin-actions">
          <${Button} title=${t('operations.admin.extendTrial')} icon="IconCalendarPlus" variant="secondary" size="small" disabled=${isWorking} onClick=${() => adjust({ status: 'TRIALING', trialEndsAt: extendedTrialEnd })} />
          <${Button} title=${t('operations.admin.activate')} icon="IconCircleCheck" variant="secondary" size="small" disabled=${isWorking} onClick=${() => adjust({ status: 'ACTIVE' })} />
          <${Button} title=${t('operations.admin.suspend')} icon="IconLock" variant="secondary" accent="danger" size="small" disabled=${isWorking} onClick=${() => setConfirmSuspend(true)} />
        </div>
        ${plans.length > 0 ? html`<form class="admin-plan-form" data-testid="admin-plan-form" onSubmit=${(/** @type {Event} */ event) => {
          event.preventDefault();
          if (planKey) adjust({ planKey });
        }}>
          <${SelectField} label=${text('changePlan')} value=${planKey} hint=${isLegacyPlan ? text('legacyPlanHint') : text('changePlanHint')} onChange=${setPlanKey}
            options=${[{ value: '', label: text('changePlanChoose') }, ...plans.map((plan) => ({ value: plan.key, label: `${plan.name} · ${formatMoney(plan.priceCents / 100)}${plan.period === 'ANNUAL' ? ` ${text('perYear')}` : ` ${text('perMonth')}`}` }))]} />
          <${Button} title=${text('applyPlan')} icon="IconSwitchHorizontal" accent="blue" size="small" type="submit" disabled=${!planKey || isWorking} />
        </form>` : null}
      </${AdminSection}>
      <${AdminSection} title=${text('providerSubscription')} description=${text('providerSubscriptionDescription')}>
        ${!billing ? html`<${AdminLoading} rows=${2} />` : providerSubscription ? html`<${DefinitionList} columns=${1} items=${[
          { label: text('providerStatus'), value: html`<${Tag} color=${providerSubscription.status === 'ACTIVE' ? 'green' : 'gray'} text=${enumLabel('providerSubscriptionStatuses', providerSubscription.status)} />` },
          { label: text('providerValue'), value: `${formatMoney(providerSubscription.value)} · ${enumLabel('cycles', providerSubscription.cycle)}` },
          { label: text('nextDueDate'), value: formatDay(providerSubscription.nextDueDate) },
          { label: t('operations.billing.billingType'), value: billingTypeLabel(providerSubscription.billingType) },
          { label: text('providerDescription'), value: providerSubscription.description || '-' },
          { label: text('providerSubscriptionId'), value: providerSubscription.id, mono: true },
        ]} />` : html`<p class="admin-muted">${billing.isProviderConfigured ? text('noProviderSubscription') : t('operations.admin.panel.provider.notConfigured')}</p>`}
      </${AdminSection}>
    </div>
    <${TenantModulesSection} tenantId=${tenantId} />
    ${confirmSuspend ? html`<${Modal} title=${text('suspendTitle')} subtitle=${text('suspendSubtitle')} confirmLabel=${t('operations.admin.suspend')} confirmAccent="danger" isLoading=${isWorking}
      onClose=${() => setConfirmSuspend(false)} onConfirm=${() => adjust({ status: 'SUSPENDED' })} />` : null}
  </div>`;
}

/** @param {{ tenantId: string, billing: any, onChanged: () => void }} props */
function InvoicesTab({ tenantId, billing, onChanged }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const settings = billing?.invoiceSettings ?? null;
  const hasProviderSubscription = Boolean(billing?.subscription?.asaasSubscriptionId) && billing?.isProviderConfigured;

  const remove = async () => {
    setIsRemoving(true);
    try {
      await removeAdminInvoiceSettings(tenantId);
      showSuccessSnackbar(text('invoiceSettingsRemoved'));
      setConfirmRemove(false);
      onChanged();
    } catch (error) {
      showOperationsError(error);
    } finally {
      setIsRemoving(false);
    }
  };

  return html`<div class="admin-tab-body">
    <${AdminSection} title=${text('automaticInvoice')} description=${text('automaticInvoiceDescription')} testId="admin-invoice-settings"
      actions=${hasProviderSubscription ? html`
        <${Button} title=${settings ? t('operations.admin.panel.common.edit') : text('configureInvoice')} icon=${settings ? 'IconSettings' : 'IconPlus'} variant="secondary" size="small" onClick=${() => setIsEditing(true)} />
        ${settings ? html`<${Button} title=${text('removeInvoiceSettings')} icon="IconTrash" variant="secondary" accent="danger" size="small" onClick=${() => setConfirmRemove(true)} />` : null}` : null}>
      ${!billing ? html`<${AdminLoading} rows=${1} />` : !hasProviderSubscription ? html`<p class="admin-muted">${billing.isProviderConfigured ? text('noProviderSubscription') : t('operations.admin.panel.provider.notConfigured')}</p>`
        : settings ? html`<${DefinitionList} columns=${3} items=${[
          { label: t('operations.admin.panel.invoiceForm.municipalService'), value: settings.municipalServiceName || '-' },
          { label: t('operations.admin.panel.invoiceForm.period'), value: enumLabel('invoicePeriods', settings.invoiceCreationPeriod) + (settings.daysBeforeDueDate ? ` (${t('operations.admin.panel.invoiceForm.days', { count: settings.daysBeforeDueDate })})` : '') },
          { label: t('operations.admin.panel.invoiceForm.receivedOnly'), value: settings.receivedOnly ? t('operations.admin.panel.common.yes') : t('operations.admin.panel.common.no') },
          { label: t('operations.admin.panel.invoiceForm.taxes'), value: ['iss', 'pis', 'cofins', 'csll', 'inss', 'ir'].map((name) => `${name.toUpperCase()} ${settings.taxes?.[name] ?? 0}%`).join(' · ') },
          { label: t('operations.admin.panel.invoiceForm.retainIss'), value: settings.taxes?.retainIss ? t('operations.admin.panel.common.yes') : t('operations.admin.panel.common.no') },
          { label: t('operations.admin.panel.invoiceForm.observations'), value: settings.observations || '-' },
        ]} />` : html`<p class="admin-muted">${text('noInvoiceSettings')}</p>`}
    </${AdminSection}>
    <${AdminSection} title=${text('issuedInvoices')}>
      <${InvoicesList} tenantId=${tenantId} showOwner=${false} embedded=${true} />
    </${AdminSection}>
    ${isEditing ? html`<${InvoiceSettingsDialog} tenantId=${tenantId} existing=${settings} onClose=${() => setIsEditing(false)} onSaved=${() => {
      setIsEditing(false);
      onChanged();
    }} />` : null}
    ${confirmRemove ? html`<${Modal} title=${text('removeInvoiceSettingsTitle')} subtitle=${text('removeInvoiceSettingsSubtitle')} confirmLabel=${text('removeInvoiceSettings')} confirmAccent="danger"
      isLoading=${isRemoving} onClose=${() => setConfirmRemove(false)} onConfirm=${remove} />` : null}
  </div>`;
}

/** @param {{ detail: any }} props */
function TeamTab({ detail }) {
  return html`<div class="admin-tab-body">
    <${AdminSection} title=${t('operations.admin.usage')}>
      ${detail.usage ? html`<${StatGrid} columns=${4}>
        ${Object.entries(detail.usage.recordCounts).map(([name, count]) => html`<${StatTile} key=${name} label=${t(`operations.admin.usageCounts.${name}`)} value=${formatInteger(/** @type {number} */ (count))} />`)}
        <${StatTile} label=${t('operations.admin.messages')} value=${formatInteger(detail.usage.messageCount)} />
        <${StatTile} label=${t('operations.admin.databaseSize')} value=${formatByteSize(detail.usage.databaseByteSize)} />
        ${detail.usage.supportMemberCount ? html`<${StatTile} label=${t('operations.admin.supportMembers')} value=${formatInteger(detail.usage.supportMemberCount)} tone="muted" />` : null}
        ${detail.usage.deactivatedSupportMemberCount ? html`<${StatTile} label=${t('operations.admin.deactivatedSupportMembers')} value=${formatInteger(detail.usage.deactivatedSupportMemberCount)} tone="warning" />` : null}
      </${StatGrid}>` : html`<p class="admin-muted">${t('operations.admin.empty')}</p>`}
    </${AdminSection}>
    <${AdminSection} title=${t('operations.admin.teamMembers')} description=${text('teamDescription', { count: detail.members.length })}>
      <div class="settings-table admin-table">
        <div class="settings-table-row settings-table-header admin-member-row"><span>${text('memberName')}</span><span>${text('memberEmail')}</span><span>${text('memberRole')}</span></div>
        ${detail.members.map((/** @type {any} */ member) => html`<div key=${member.email} class="settings-table-row admin-member-row">
          <span>${[member.firstName, member.lastName].filter(Boolean).join(' ') || '-'}</span>
          <span class="settings-table-muted">${member.email}</span>
          <span><${Tag} color=${member.role === 'ADMIN' ? 'blue' : 'gray'} text=${enumLabel('memberRoles', member.role)} /></span>
        </div>`)}
      </div>
    </${AdminSection}>
  </div>`;
}

/** @param {{ detail: any }} props */
function OperationsTab({ detail }) {
  return html`<div class="admin-tab-body">
    <${AdminSection} title=${t('operations.admin.backupsTitle')}>
      <${BackupTable} runs=${detail.backups} />
    </${AdminSection}>
    <${AdminSection} title=${t('operations.admin.auditTitle')} description=${text('auditDescription')}>
      ${detail.audit.length === 0 ? html`<p class="admin-muted">${t('operations.admin.empty')}</p>` : html`<ul class="admin-feed">
        ${detail.audit.map((/** @type {any} */ entry) => html`<li key=${entry.id} class="admin-feed-item">
          <span class="admin-feed-main"><span>${auditActionLabel(entry)}</span><span class="settings-table-muted">${entry.actorEmail}</span></span>
          <span class="admin-feed-side"><span class="settings-table-muted">${formatDateTime(entry.createdAt)}</span></span>
        </li>`)}
      </ul>`}
    </${AdminSection}>
  </div>`;
}

/**
 * Zona de perigo da ficha (3.4.0, contrato 08, seção 7.5): excluir a instituição pede o endereço digitado e um motivo
 * opcional; depois da exclusão a lista de instituições abre sem ela.
 * @param {{ tenant: { id: string, slug: string, displayName: string }, onClose: () => void }} props
 */
function DeleteInstitutionDialog({ tenant, onClose }) {
  const [confirmSlug, setConfirmSlug] = useState('');
  const [reason, setReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const matches = confirmSlug.trim() === tenant.slug;
  const remove = async () => {
    if (!matches) return;
    setIsDeleting(true);
    try {
      const result = await deleteAdminTenant(tenant.id, { confirmSlug: confirmSlug.trim(), reason: reason.trim() });
      showSuccessSnackbar(result.warnings.length > 0 ? text('deletedWithWarnings', { name: tenant.displayName }) : text('deleted', { name: tenant.displayName }));
      navigate(adminPath('institutions'));
    } catch (error) {
      showOperationsError(error);
      setIsDeleting(false);
    }
  };
  return html`<${Modal} title=${text('deleteTitle', { name: tenant.displayName })} subtitle=${text('deleteSubtitle')} confirmLabel=${text('deleteConfirm')} confirmAccent="danger"
    isConfirmDisabled=${!matches} isLoading=${isDeleting} onClose=${onClose} onConfirm=${remove}>
    <div class="admin-delete-form" data-testid="admin-delete-form">
      <${TextField} label=${text('deleteSlugLabel', { slug: tenant.slug })} value=${confirmSlug} placeholder=${tenant.slug} autoFocus=${true}
        onInput=${(/** @type {string} */ value) => setConfirmSlug(value)} />
      <${TextField} label=${text('deleteReasonLabel')} value=${reason} maxLength=${500} onInput=${(/** @type {string} */ value) => setReason(value)} />
    </div>
  </${Modal}>`;
}

/** @param {{ tenantId: string, billing: any, tenant: { id: string, slug: string, displayName: string } }} props */
function SupportTab({ tenantId, billing, tenant }) {
  const [reason, setReason] = useState('');
  const [isWorking, setIsWorking] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const enterAsSupport = async (/** @type {Event} */ event) => {
    event.preventDefault();
    setIsWorking(true);
    try {
      const { redirectUrl } = await startSupportSession(tenantId, reason.trim());
      window.open(redirectUrl, '_blank', 'noopener');
    } catch (error) {
      showOperationsError(error);
    } finally {
      setIsWorking(false);
    }
  };
  return html`<div class="admin-tab-body">
    <${AdminSection} title=${t('operations.admin.supportTitle')} description=${t('operations.admin.supportDescription')}>
      <form class="admin-support-form" onSubmit=${enterAsSupport}>
        <input class="text-input" type="text" value=${reason} placeholder=${t('operations.admin.supportReason')} aria-label=${t('operations.admin.supportReason')}
          onInput=${(/** @type {Event} */ event) => setReason(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
        <${Button} title=${t('operations.admin.supportEnter')} icon="IconHeadset" accent="blue" size="small" type="submit" disabled=${!reason.trim() || isWorking} />
      </form>
    </${AdminSection}>
    ${billing?.siteOrders?.length ? html`<${AdminSection} title=${text('siteOrdersTitle')}>
      <ul class="admin-feed">
        ${billing.siteOrders.map((/** @type {any} */ order) => html`<li key=${order.id} class="admin-feed-item">
          <${Link} href=${adminPath('site-orders', order.id)} className="admin-feed-main"><span>${order.modulesLabel} (${enumLabel('periods', order.period)})</span><span class="settings-table-muted">${formatDateTime(order.createdAt)} · ${formatMoney(order.amounts?.today)}</span></${Link}>
          <span class="admin-feed-side"><${SiteOrderStatusTag} status=${order.status} /></span>
        </li>`)}
      </ul>
    </${AdminSection}>` : null}
    <${AdminSection} title=${text('dangerZone')} description=${text('deleteDescription')} testId="admin-danger-zone">
      <div><${Button} title=${text('deleteButton')} icon="IconTrash" variant="secondary" accent="danger" size="small" onClick=${() => setIsDeleteOpen(true)} /></div>
    </${AdminSection}>
    ${isDeleteOpen ? html`<${DeleteInstitutionDialog} tenant=${tenant} onClose=${() => setIsDeleteOpen(false)} />` : null}
  </div>`;
}

/** @param {{ tenantId: string }} props */
export function AdminInstitutionPage({ tenantId }) {
  const [detail, setDetail] = useState(/** @type {any} */ (null));
  const [billing, setBilling] = useState(/** @type {any} */ (null));
  const [isMissing, setIsMissing] = useState(false);
  const tab = resolveInstitutionTab(searchParamsState.value.get('tab'));

  const load = () => {
    fetchAdminTenant(tenantId).then(setDetail).catch((error) => {
      if (/** @type {any} */ (error)?.statusCode === 404) setIsMissing(true);
      else showOperationsError(error);
    });
    fetchAdminTenantBilling(tenantId).then(setBilling).catch(showOperationsError);
  };
  useEffect(() => {
    setDetail(null);
    setBilling(null);
    setIsMissing(false);
    load();
  }, [tenantId]);

  const back = { href: adminPath('institutions'), label: t('common.back') };
  if (isMissing) {
    return html`<${AdminPage} icon="IconBuilding" title=${t('operations.admin.panel.sections.institutions')} back=${back}>
      <${AdminEmpty} icon="IconBuilding" title=${text('notFound')} />
    </${AdminPage}>`;
  }
  const header = detail ? html`<div class="admin-headline"><${SubscriptionStatusTag} status=${detail.subscriptionState.status} />
    <${ExternalLink} href=${`${window.location.protocol}//${detail.tenant.slug}.${window.location.host.replace(/^app\./, '')}`} label=${text('openSite')} /></div>` : null;
  return html`<${AdminPage} icon="IconBuilding" title=${detail ? detail.tenant.displayName : '…'} back=${back} actions=${header} wide=${true}>
    ${!detail ? html`<${AdminLoading} rows=${4} />` : html`
      <p class="admin-subtitle">${detail.tenant.slug} · ${text('since', { date: formatDate(detail.tenant.createdAt) })} · ${detail.planName ?? detail.subscription?.planKey ?? t('operations.billing.statuses.LEGACY')}</p>
      <${TabList} activeTabId=${tab} onChange=${(/** @type {string} */ next) => updateSearchParams({ tab: next === ADMIN_INSTITUTION_TABS[0] ? null : next })}
        tabs=${ADMIN_INSTITUTION_TABS.map((key) => ({ id: key, title: text(`tabs.${key}`), icon: { summary: 'IconBuilding', subscription: 'IconCreditCard', payments: 'IconReceipt', invoices: 'IconFileText', team: 'IconUsers', operations: 'IconDatabase', support: 'IconHeadset' }[key] }))} />
      ${tab === 'summary' ? html`<${SummaryTab} detail=${detail} billing=${billing} />` : null}
      ${tab === 'subscription' ? html`<${SubscriptionTab} tenantId=${tenantId} detail=${detail} billing=${billing} onChanged=${load} />` : null}
      ${tab === 'payments' ? html`<div class="admin-tab-body"><${ProviderNotice} isProviderConfigured=${billing?.isProviderConfigured ?? true} error=${billing?.providerError} /><${PaymentsList} tenantId=${tenantId} showOwner=${false} embedded=${true} /></div>` : null}
      ${tab === 'invoices' ? html`<${InvoicesTab} tenantId=${tenantId} billing=${billing} onChanged=${load} />` : null}
      ${tab === 'team' ? html`<${TeamTab} detail=${detail} />` : null}
      ${tab === 'operations' ? html`<${OperationsTab} detail=${detail} />` : null}
      ${tab === 'support' ? html`<${SupportTab} tenantId=${tenantId} billing=${billing} tenant=${detail.tenant} />` : null}
    `}
  </${AdminPage}>`;
}
